import { createFeatureFlags } from '@sitecore/featureflags';

export const defaultFeatureFlags = {
  MFA: true,
  SNOW: true,
  APP_SWITCHER: true,
  EXPORT_MEMBERS: true,
  INVITATIONS: true,
  SSO: true,
  NEW_FEATURE_ANNOUNCEMENT: false,
  SUBSCRIPTIONS: false,
  SAML: false,
  OPENID: false,
  PROVISION_APPS: false,
  PROVISION_STREAM: false,
  RECOMMENDATIONS: false,
  SUPPORT_ACCESS: false,
  CONTEXT_MAPPING: false,
  SCOPED_CONTEXT_MAPPING: false,
  CUSTOM_HOSTNAMES: false,
  CLAIMS_MAPPING: false,
  MARKETPLACE: false,
  UNIFIED_TRACKING: false,
  KC_BOT: false,
  AUDIT_LOGS: false,
  CONTENT_HUB_TOGGLE: false,
};

export const {
  Feature,
  FlagsProvider,
  useFeature,
  useFeatures,
  withFeature,
  behindFeature,
} = createFeatureFlags(defaultFeatureFlags);

export type FeatureFlags = ReturnType<typeof useFeatures>;
export type FeatureName = keyof FeatureFlags;

interface Organization {
  [id: string]: string | string[];
}

export interface EnvironmentFlags {
  MFA: Organization;
  SNOW: Organization;
  APP_SWITCHER: Organization;
  EXPORT_MEMBERS: Organization;
  INVITATIONS: Organization;
  SSO: Organization;
  NEW_FEATURE_ANNOUNCEMENT: Organization;
  SUBSCRIPTIONS: Organization;
  SAML: Organization;
  OPENID: Organization;
  PROVISION_APPS: Organization;
  PROVISION_STREAM: Organization;
  RECOMMENDATIONS: Organization;
  SUPPORT_ACCESS: Organization;
  CONTEXT_MAPPING: Organization;
  SCOPED_CONTEXT_MAPPING: Organization;
  CUSTOM_HOSTNAMES: Organization;
  CLAIMS_MAPPING: Organization;
  MARKETPLACE: Organization;
  UNIFIED_TRACKING: Organization;
  KC_BOT: Organization;
  AUDIT_LOGS: Organization;
  CONTENT_HUB_TOGGLE: Organization;
}

export interface Environments {
  dev: EnvironmentFlags;
  qa: EnvironmentFlags;
  staging: EnvironmentFlags;
  preprod: EnvironmentFlags;
  prod: EnvironmentFlags;
}

//todo flag ids not to disclosure the feature name
export const organizationConditionsPerEnvironment: Environments = {
  dev: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    MARKETPLACE: {},
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
  },
  qa: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    MARKETPLACE: {},
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
  },
  staging: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: { org_NngeQKXoFUObloUL: 'customer-portal' },
    SUBSCRIPTIONS: {
      org_Lztbh9wBHCl2oyBL: 'test-app-organization-0lq01b',
      org_gM6bfxVu8y5MHfEp: 'ops-demo-org',
    },
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {},
    PROVISION_STREAM: {
      org_Lztbh9wBHCl2oyBL: 'This is the test app org',
      org_FijrH7yaknrqmRcI: 'Portal Org',
    },
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    MARKETPLACE: {
      org_NngeQKXoFUObloUL: 'Customer Portal',
      orgTypeFilter: ['internal'],
    },
    UNIFIED_TRACKING: {
      org_8UX5nhL9vs961j2R: 'CDPP Development Org',
      org_IKF49tqy6kcfWN2Z: 'DevEx Organization',
    },
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
  },
  preprod: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {},
    SAML: {},
    OPENID: {},
    PROVISION_APPS: { orgTypeFilter: ['internal'] },
    PROVISION_STREAM: {},
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: {},
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    MARKETPLACE: {
      org_bAqmInqwbyS4uEbb: 'Marketplace Demo',
      org_SFkrSSu3YvI34waF: 'Marketplace QA',
    },
    UNIFIED_TRACKING: {},
    KC_BOT: {},
    AUDIT_LOGS: {},
    CONTENT_HUB_TOGGLE: {},
  },
  prod: {
    MFA: {},
    SNOW: {},
    APP_SWITCHER: {},
    EXPORT_MEMBERS: {},
    INVITATIONS: {},
    SSO: {},
    NEW_FEATURE_ANNOUNCEMENT: {},
    SUBSCRIPTIONS: {
      org_E2A54NDGy5IMoO5X: 'Emirates',
      org_fcZMCki4V9rCDzjB: 'Xmcloud Customer Test',
    },
    SAML: {},
    OPENID: {},
    PROVISION_APPS: {
      org_xTo4C6tP8EnLJTLV: 'XMCloud Deploy UI Organization',
      org_fcZMCki4V9rCDzjB: 'Xmcloud Customer Test',
    },
    PROVISION_STREAM: { orgTypeFilter: ['unspecified', 'partner', 'customer'] },
    RECOMMENDATIONS: {},
    SUPPORT_ACCESS: {},
    CONTEXT_MAPPING: { orgTypeFilter: ['internal'] },
    SCOPED_CONTEXT_MAPPING: {},
    CUSTOM_HOSTNAMES: {},
    CLAIMS_MAPPING: {},
    MARKETPLACE: {
      org_DLZHus8Dxa2xVgt8: 'Valtech SE',
      org_wchRoFk3LhERwfhV: 'Valir Studio',
      org_mljyOkIciNmbYZyp: 'Transperfect',
      org_ymNaHJXSK6ca26Gz: 'Anna & Christian Hahn',
      org_y6ngZN2a0nvpSxFL: 'Uxbee / RAI',
      org_TLeyvmqOAW5LiDPf: 'Altudo',
      org_lt6yYWsNb2KUH8Zx: 'Horizontal',
      org_SsTGHxTPm1fLhAvZ: 'Konabos',
      org_9EFrOXbeHAwWV9nu: 'Globalization Partners International',
      org_1K1gNYhUlXDlxd3x: 'CPH Mist',
      orgTypeFilter: ['internal'],
    },
    UNIFIED_TRACKING: { org_7qihOdknvMrfI0hj: 'Personalize Team' },
    KC_BOT: {},
    AUDIT_LOGS: { orgTypeFilter: ['internal'] },
    CONTENT_HUB_TOGGLE: {},
  },
};
